import type {
  BackgroundImage,
  Controller,
  ControllerSet,
  CustomShape,
  DocumentModel,
  ImageController,
  PathDef,
  ShapeLayer,
  Vec2,
} from '../core/types'
import { defaultDocument, defaultShapeLayer } from '../core/types'

export function exportJson(doc: DocumentModel): string {
  return JSON.stringify({ app: 'ventholes', document: doc }, null, 2)
}

function coerceVec2(raw: unknown, fallback: Vec2): Vec2 {
  const v = raw as { x?: unknown; y?: unknown } | null
  const x = typeof v?.x === 'number' && Number.isFinite(v.x) ? v.x : fallback.x
  const y = typeof v?.y === 'number' && Number.isFinite(v.y) ? v.y : fallback.y
  return { x, y }
}

// Fill v2 defaults on a controller from any older or partial file. Unknown
// kinds and malformed entries are dropped rather than guessed at.
function coerceController(raw: unknown): Controller | null {
  if (typeof raw !== 'object' || raw === null) return null
  const c = raw as Record<string, unknown>
  const kind = c.kind
  if (kind !== 'point' && kind !== 'line' && kind !== 'curve' && kind !== 'polyline') return null
  const a = coerceVec2(c.a, { x: 0, y: 0 })
  const nodes = Array.isArray(c.nodes) ? c.nodes.map((p) => coerceVec2(p, { x: 0, y: 0 })) : []
  if ((kind === 'curve' || kind === 'polyline') && nodes.length < 2) return null
  const falloff = c.falloff
  return {
    id: typeof c.id === 'string' ? c.id : 'c' + Math.random().toString(36).slice(2, 10),
    kind,
    a,
    b: coerceVec2(c.b, a),
    nodes,
    value: typeof c.value === 'number' && Number.isFinite(c.value) ? c.value : 1,
    radius: typeof c.radius === 'number' && Number.isFinite(c.radius) ? c.radius : 25,
    falloff:
      falloff === 'linear' || falloff === 'smooth' || falloff === 'sharp' || falloff === 'soft'
        ? falloff
        : 'smooth',
    hardEdge: c.hardEdge === true,
    enabled: c.enabled !== false,
    singleSide: c.singleSide === 'left' || c.singleSide === 'right' ? c.singleSide : 'off',
    infinite: c.infinite === true,
    syncGroup: typeof c.syncGroup === 'string' ? c.syncGroup : null,
  }
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

function coerceImage(raw: unknown): ImageController | null {
  if (typeof raw !== 'object' || raw === null) return null
  const i = raw as Record<string, unknown>
  if (typeof i.dataUri !== 'string' || !i.dataUri.startsWith('data:image/')) return null
  const r = (i.rect ?? {}) as Record<string, unknown>
  return {
    enabled: i.enabled !== false,
    dataUri: i.dataUri,
    rect: { x: num(r.x, 0), y: num(r.y, 0), w: num(r.w, 100), h: num(r.h, 100) },
    invert: i.invert === true,
    contrast: Math.min(1, Math.max(-1, num(i.contrast, 0))),
    valueLo: num(i.valueLo, 1),
    valueHi: num(i.valueHi, 5),
  }
}

function coerceBackground(raw: unknown): BackgroundImage | null {
  if (typeof raw !== 'object' || raw === null) return null
  const b = raw as Record<string, unknown>
  if (typeof b.dataUri !== 'string' || !b.dataUri.startsWith('data:image/')) return null
  return {
    dataUri: b.dataUri,
    opacity: Math.min(1, Math.max(0, num(b.opacity, 0.5))),
    width: Math.max(1, num(b.width, 100)),
    x: num(b.x, 0),
    y: num(b.y, 0),
    visible: b.visible !== false,
  }
}

function coercePath(raw: unknown): PathDef | null {
  if (typeof raw !== 'object' || raw === null) return null
  const p = raw as Record<string, unknown>
  if (p.kind !== 'curve' && p.kind !== 'polyline') return null
  const nodes = Array.isArray(p.nodes) ? p.nodes.map((n) => coerceVec2(n, { x: 0, y: 0 })) : []
  if (nodes.length < 2) return null
  return {
    id: typeof p.id === 'string' ? p.id : 'p' + Math.random().toString(36).slice(2, 10),
    kind: p.kind,
    nodes,
  }
}

const LAYER_KINDS = ['circle', 'rect', 'slot', 'polygon', 'star', 'outline'] as const

function coerceLayer(raw: unknown): ShapeLayer | null {
  if (typeof raw !== 'object' || raw === null) return null
  const l = raw as Record<string, unknown>
  const kind = LAYER_KINDS.find((k) => k === l.kind)
  if (!kind) return null
  const base = defaultShapeLayer(kind)
  const outline = Array.isArray(l.outline) ? l.outline.map((n) => coerceVec2(n, { x: 0, y: 0 })) : []
  if (kind === 'outline' && outline.length < 3) return null
  return {
    ...base,
    id: typeof l.id === 'string' ? l.id : base.id,
    role: l.role === 'subtract' ? 'subtract' : 'union',
    x: num(l.x, base.x),
    y: num(l.y, base.y),
    w: Math.max(0.001, num(l.w, base.w)),
    h: Math.max(0.001, num(l.h, base.h)),
    rotation: num(l.rotation, 0),
    cornerRadius: Math.max(0, num(l.cornerRadius, 0)),
    sides: Math.max(3, Math.round(num(l.sides, base.sides))),
    starPoints: Math.max(3, Math.round(num(l.starPoints, base.starPoints))),
    starInner: Math.min(0.95, Math.max(0.1, num(l.starInner, base.starInner))),
    mirrorH: l.mirrorH === true,
    mirrorV: l.mirrorV === true,
    outline,
  }
}

function coerceCustomShape(raw: unknown): CustomShape {
  const cs = raw as { layers?: unknown } | null
  return {
    layers: Array.isArray(cs?.layers)
      ? cs.layers.map(coerceLayer).filter((l): l is ShapeLayer => l !== null)
      : [],
  }
}

function coerceSet(raw: unknown, base: ControllerSet): ControllerSet {
  const s = raw as { base?: unknown; controllers?: unknown; image?: unknown } | null
  return {
    base: num(s?.base, base.base),
    controllers: Array.isArray(s?.controllers)
      ? s.controllers.map(coerceController).filter((c): c is Controller => c !== null)
      : [],
    image: coerceImage(s?.image),
  }
}

// Merge a parsed document over the defaults so older or partial files load.
// Any accepted document is migrated forward to the current schema version.
export function coerceDocument(raw: unknown): DocumentModel | null {
  if (typeof raw !== 'object' || raw === null) return null
  const d = raw as Record<string, unknown>
  const base = defaultDocument()
  const merged: DocumentModel = {
    ...base,
    ...(d as Partial<DocumentModel>),
    canvas: { ...base.canvas, ...(d.canvas as object | undefined) },
    boundary: {
      ...base.boundary,
      ...(d.boundary as object | undefined),
      nodes: Array.isArray((d.boundary as any)?.nodes)
        ? ((d.boundary as any).nodes as unknown[]).map((n) => coerceVec2(n, { x: 0, y: 0 }))
        : [],
    },
    shape: { ...base.shape, ...(d.shape as object | undefined) },
    shapeB: { ...base.shapeB, ...(d.shapeB as object | undefined) },
    customShape: coerceCustomShape(d.customShape),
    layout: {
      ...base.layout,
      ...(d.layout as object | undefined),
      grid: { ...base.layout.grid, ...((d.layout as any)?.grid as object | undefined) },
      crosshatch: {
        ...base.layout.crosshatch,
        ...((d.layout as any)?.crosshatch as object | undefined),
      },
      voronoi: { ...base.layout.voronoi, ...((d.layout as any)?.voronoi as object | undefined) },
      flow: { ...base.layout.flow, ...((d.layout as any)?.flow as object | undefined) },
      paths: Array.isArray((d.layout as any)?.paths)
        ? ((d.layout as any).paths as unknown[])
            .map(coercePath)
            .filter((p): p is PathDef => p !== null)
        : [],
    },
    channels: {
      size: coerceSet((d.channels as any)?.size, base.channels.size),
      spacing: coerceSet((d.channels as any)?.spacing, base.channels.spacing),
      angle: coerceSet((d.channels as any)?.angle, base.channels.angle),
      shape: coerceSet((d.channels as any)?.shape, base.channels.shape),
    },
    background: coerceBackground(d.background),
    guides: Array.isArray(d.guides)
      ? (d.guides as unknown[])
          .map((raw) => {
            if (typeof raw !== 'object' || raw === null) return null
            const g = raw as Record<string, unknown>
            const axis = g.axis
            if (axis !== 'x' && axis !== 'y') return null
            if (typeof g.value !== 'number' || !Number.isFinite(g.value)) return null
            return {
              id: typeof g.id === 'string' ? g.id : 'g' + Math.random().toString(36).slice(2, 10),
              axis: axis as 'x' | 'y',
              value: g.value,
            }
          })
          .filter((g): g is NonNullable<typeof g> => g !== null)
      : [],
    clip:
      d.clip === 'uniform' || d.clip === 'clip' || d.clip === 'dropOutside' || d.clip === 'dropTouching'
        ? d.clip
        : base.clip,
    clipMargin: Math.max(0, num(d.clipMargin, 0)),
    export: { ...base.export, ...(d.export as object | undefined) },
    version: 2,
  }
  if (!Number.isFinite(merged.canvas.width) || !Number.isFinite(merged.canvas.height)) return null
  return merged
}

export function parseDocumentFile(text: string): DocumentModel | null {
  // Accept a plain document JSON, the wrapped export format, or an exported
  // SVG with an embedded document.
  const trimmed = text.trimStart()
  if (trimmed.startsWith('<')) {
    const embedded = extractSvgDocument(text)
    return embedded ? coerceDocument(embedded) : null
  }
  try {
    const parsed = JSON.parse(text)
    if (parsed && typeof parsed === 'object' && 'document' in parsed) {
      return coerceDocument((parsed as { document: unknown }).document)
    }
    return coerceDocument(parsed)
  } catch {
    return null
  }
}

export function extractSvgDocument(svgText: string): unknown | null {
  const m = svgText.match(/<metadata id="ventholes-document">([\s\S]*?)<\/metadata>/)
  if (!m) return null
  const json = m[1].replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
  try {
    return JSON.parse(json)
  } catch {
    return null
  }
}
